import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Modal } from "./Modal";
import { request } from "../lib/api";

interface InitialClient {
  id: string;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  legal_name: string;
  email: string;
  phone?: string | null;
  dob_masked?: string | null;
  ssn_masked?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  monitoring?: string | null;
  monitoring_username?: string | null;
  cfpb_login_email?: string | null;
  score_start?: number | null;
  score_current?: number | null;
  notes?: string | null;
  status?: string | null;
}

export function EditClientDialog({
  open,
  onClose,
  onSaved,
  client,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (updated: any) => void;
  client: InitialClient;
}) {
  const [firstName, setFirstName]   = useState(client.first_name || "");
  const [middleName, setMiddleName] = useState(client.middle_name || "");
  const [lastName, setLastName]     = useState(client.last_name || "");
  const [email, setEmail]           = useState(client.email || "");
  const [phone, setPhone]           = useState(client.phone || "");
  const [dob, setDob]               = useState("");           // new value only; blank = keep existing
  const [ssn, setSsn]               = useState("");           // new value only; blank = keep existing
  const [addr1, setAddr1]           = useState(client.address_line1 || "");
  const [addr2, setAddr2]           = useState(client.address_line2 || "");
  const [city, setCity]             = useState(client.address_city || "");
  const [stateCode, setStateCode]   = useState(client.address_state || "");
  const [zip, setZip]               = useState(client.address_zip || "");
  const [monitoring, setMonitoring] = useState((client.monitoring || "not_started") as
    "not_started" | "my_free_score_now" | "smart_credit");
  const [monUser, setMonUser]       = useState(client.monitoring_username || "");
  const [monPass, setMonPass]       = useState("");           // blank = keep existing
  const [showMonPass, setShowMonPass] = useState(false);
  const [cfpbEmail, setCfpbEmail]   = useState(client.cfpb_login_email || "");
  const [cfpbPass, setCfpbPass]     = useState("");           // blank = keep existing
  const [showCfpbPass, setShowCfpbPass] = useState(false);
  const [scoreStart, setScoreStart] = useState(client.score_start != null ? String(client.score_start) : "");
  const [scoreCurr, setScoreCurr]   = useState(client.score_current != null ? String(client.score_current) : "");
  const [notes, setNotes]           = useState(client.notes || "");
  const [status, setStatus]         = useState((client.status as string) || "pending");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // MyFreeScoreNow logs in with an email. A name here saves fine and then every
  // import fails with "Please enter required fields" — catch it at the form.
  const monUserInvalid =
    monitoring === "my_free_score_now" &&
    monUser.trim().length > 0 &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(monUser.trim());

  async function save() {
    if (monUserInvalid) return;
    setBusy(true); setError(null);

    const patch: any = {
      first_name:  firstName.trim(),
      middle_name: middleName.trim() || null,
      last_name:   lastName.trim(),
      email:       email.trim().toLowerCase(),
      phone:       phone.trim() || null,
      address_line1: addr1.trim() || null,
      address_line2: addr2.trim() || null,
      address_city:  city.trim() || null,
      address_state: stateCode.trim() || null,
      address_zip:   zip.trim() || null,
      monitoring,
      monitoring_username: monUser.trim() || null,
      score_start: scoreStart === "" ? null : Number(scoreStart),
      score_current: scoreCurr === "" ? null : Number(scoreCurr),
      notes: notes.trim() || null,
      status,
    };
    if (dob) patch.dob = dob;
    if (ssn.replace(/[^0-9]/g, "").length === 9) patch.ssn_full = ssn.replace(/[^0-9]/g, "");
    if (monPass) patch.monitoring_password = monPass;
    patch.cfpb_login_email = cfpbEmail.trim() || null;
    if (cfpbPass) patch.cfpb_password = cfpbPass;

    try {
      const r = await request<{ ok: true; client: any }>(`/api/clients/${client.id}`, {
        method: "PATCH",
        body: patch,
      });
      onSaved(r.client);
    } catch (e: any) {
      setError(e?.message || "Could not save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit client"
      footer={
        <>
          <button onClick={onClose} className="ghost-button">Cancel</button>
          <button onClick={save} className="primary-button" disabled={busy || monUserInvalid}>
            {busy ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      <div className="stacked-form" style={{ maxHeight: 560, overflowY: "auto", paddingRight: 6 }}>
        <h4 style={{ margin: "0 0 6px" }}>Status</h4>
        <label className="form-field">
          <span>Client status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="pending">Pending — intake not sent yet</option>
            <option value="active">Active — intake signed</option>
            <option value="suspended">Suspended — e.g. non-payment</option>
            <option value="completed">Completed</option>
            <option value="churned">Churned</option>
          </select>
        </label>

        <h4 style={{ margin: "0 0 6px" }}>Name</h4>
        <div className="form-row-3">
          <label className="form-field"><span>First *</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} /></label>
          <label className="form-field"><span>Middle</span>
            <input value={middleName} onChange={(e) => setMiddleName(e.target.value)} /></label>
          <label className="form-field"><span>Last *</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} /></label>
        </div>

        <h4>Contact</h4>
        <div className="form-row-2">
          <label className="form-field"><span>Email *</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <label className="form-field"><span>Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} /></label>
        </div>

        <h4>Identity</h4>
        <div className="form-row-2">
          <label className="form-field">
            <span>Date of birth {client.dob_masked && <em className="muted small">(current: {client.dob_masked} — leave blank to keep)</em>}</span>
            <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
          </label>
          <label className="form-field">
            <span>SSN {client.ssn_masked && <em className="muted small">(current: {client.ssn_masked} — leave blank to keep)</em>}</span>
            <input
              value={ssn}
              onChange={(e) => setSsn(e.target.value)}
              placeholder="123-45-6789"
              autoComplete="off"
            />
          </label>
        </div>

        <h4>Address</h4>
        <label className="form-field"><span>Street</span>
          <input value={addr1} onChange={(e) => setAddr1(e.target.value)} /></label>
        <label className="form-field"><span>Apt / Suite</span>
          <input value={addr2} onChange={(e) => setAddr2(e.target.value)} /></label>
        <div className="form-row-3">
          <label className="form-field"><span>City</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} /></label>
          <label className="form-field"><span>State</span>
            <input value={stateCode} onChange={(e) => setStateCode(e.target.value)} maxLength={2} /></label>
          <label className="form-field"><span>ZIP</span>
            <input value={zip} onChange={(e) => setZip(e.target.value)} maxLength={10} /></label>
        </div>

        <h4>Credit monitoring</h4>
        <div className="form-row-2">
          <label className="form-field"><span>Provider</span>
            <select value={monitoring} onChange={(e) => setMonitoring(e.target.value as any)}>
              <option value="not_started">Not started</option>
              <option value="my_free_score_now">MyFreeScoreNow</option>
              <option value="smart_credit">SmartCredit</option>
            </select>
          </label>
          <label className="form-field"><span>Starting score</span>
            <input value={scoreStart} onChange={(e) => setScoreStart(e.target.value)} inputMode="numeric" /></label>
        </div>
        {monitoring !== "not_started" && (
          <div className="form-row-2">
            <label className="form-field">
              <span>{monitoring === "my_free_score_now" ? "Login email" : "Username"}</span>
              <input
                value={monUser}
                onChange={(e) => setMonUser(e.target.value)}
                autoComplete="off"
                type={monitoring === "my_free_score_now" ? "email" : "text"}
                placeholder={monitoring === "my_free_score_now" ? "the email they log in with" : ""}
                aria-invalid={monUserInvalid || undefined}
              />
              {monUserInvalid && (
                <em className="small" style={{ color: "#b3382a" }}>
                  MyFreeScoreNow signs in with an email address — not the client&rsquo;s name.
                </em>
              )}
            </label>
            <label className="form-field">
              <span>Password <em className="muted small">(leave blank to keep)</em></span>
              <div className="input-with-affix">
                <input
                  type={showMonPass ? "text" : "password"}
                  value={monPass}
                  onChange={(e) => setMonPass(e.target.value)}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="input-affix-button"
                  onClick={() => setShowMonPass(s => !s)}
                  title={showMonPass ? "Hide password" : "Show password"}
                  aria-label={showMonPass ? "Hide password" : "Show password"}
                  aria-pressed={showMonPass}
                >
                  {showMonPass ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                </button>
              </div>
            </label>
          </div>
        )}

        <h4>CFPB portal login <em className="muted small">(staff only — the client never sees this)</em></h4>
        <p className="muted small" style={{ margin: "0 0 4px" }}>
          Used by the Online Dispute automation to file CFPB complaints for this client. Any email provider is fine.
        </p>
        <div className="form-row-2">
          <label className="form-field"><span>CFPB login email</span>
            <input
              type="email"
              value={cfpbEmail}
              onChange={(e) => setCfpbEmail(e.target.value)}
              autoComplete="off"
              placeholder="client-cfpb@example.com"
            /></label>
          <label className="form-field">
            <span>CFPB password <em className="muted small">(leave blank to keep)</em></span>
            <div className="input-with-affix">
              <input
                type={showCfpbPass ? "text" : "password"}
                value={cfpbPass}
                onChange={(e) => setCfpbPass(e.target.value)}
                autoComplete="off"
              />
              <button
                type="button"
                className="input-affix-button"
                onClick={() => setShowCfpbPass(s => !s)}
                title={showCfpbPass ? "Hide password" : "Show password"}
                aria-label={showCfpbPass ? "Hide password" : "Show password"}
                aria-pressed={showCfpbPass}
              >
                {showCfpbPass ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
              </button>
            </div>
          </label>
        </div>

        <h4>Score & notes</h4>
        <div className="form-row-2">
          <label className="form-field"><span>Current score</span>
            <input value={scoreCurr} onChange={(e) => setScoreCurr(e.target.value)} inputMode="numeric" /></label>
          <label className="form-field"><span>Notes</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        </div>

        {error && <p className="error-text">{error}</p>}
      </div>
    </Modal>
  );
}
